import React, { useMemo, useState } from "react";

import { useSearchParams, useNavigate } from "react-router-dom";

import {
  Button,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Stack
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

import { useGetCurrentUser, useGetOrganisation } from "../../store";
import { userHasPermission } from "../../types-helpers"
import { UserType } from "../../types";

import MainLayout from "../../layouts/main-layout";
import GeneralSection from "./sections/general";
import UsersSection from "./sections/users";
import TeamChip from "../../components/chips/team-chip";
import AddUserModal from "./sections/users/add-member-modal";


const TeamPage: React.FC = () => {
  const { data: currentUser, isLoading: userLoading } = useGetCurrentUser();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(0);
  const [addMemberOpen, setAddMemberOpen] = React.useState(false);

  // Navigation handler
  const handleBackClick = () => {
    navigate("/teams");
  };

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [searchParams] = useSearchParams();
  const [teamId] = useState<string>(searchParams.get("teamId") || "")

  const finalId: string = useMemo(() => {

    if (!currentUser || userLoading) {
      return ""
    }

    if (!teamId) {
      return currentUser.organisationIds[0]
    }

    if (currentUser.organisationIds.includes(teamId)) {
      return teamId
    }

    if (userHasPermission(UserType.SysAdmin, currentUser)) {
      return teamId
    }

    return currentUser.organisationIds[0]
  }, [currentUser, userLoading])


  const { data: finalOrganisation = null, isLoading: organisationLoading } =
    useGetOrganisation(finalId);

  const canEditMembers = useMemo(() => {
    if (!currentUser) {
      return false;
    }

    if (userHasPermission(UserType.SysAdmin, currentUser)) {
      return true;
    }

    if (!finalOrganisation) {
      return false;
    }

    return finalOrganisation.adminIds.includes(currentUser.userId);
  }, [currentUser, finalOrganisation]);

  // get normal color of text form mui theme
  const theme = useTheme();
  const normalTextColor = theme.palette.text.primary;

  return (
    <MainLayout>
      <Stack spacing={2} pb={4}>
        <Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "start", sm: "center" }}
            sx={{ pb: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="h3"
                color={normalTextColor}
                onClick={handleBackClick}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.7,
                    textDecoration: "underline"
                  }
                }}
              >
                Teams
              </Typography>
              <ChevronRightIcon
                sx={{ color: normalTextColor, fontSize: "1.5rem" }}
              />
              {!organisationLoading && (
                <TeamChip
                  teamId={finalOrganisation?.organisationId || "guest"}
                  name={finalOrganisation?.name || "Guest"}
                  color={finalOrganisation?.color || "#1976d2"}
                />
              )}
            </Stack>

            {tabValue === 0 && (
              <Button
                variant="contained"
                onClick={() => setAddMemberOpen(true)}
                startIcon={<AddIcon />}
                disabled={!canEditMembers || !finalId}
                sx={{
                  borderRadius: "1.5rem",
                  px: 2,
                  py: 0.8,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Add Member
              </Button>
            )}
          </Stack>
          <Stack sx={{ width: '100%' }}>
            <Stack sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="organization sections" variant="fullWidth">
                <Tab label={
                  <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <PeopleAltIcon />
                    <Typography sx={{ fontWeight: 'bold' }}>Members</Typography>
                  </Stack>
                } />
                <Tab disabled={!Boolean(finalOrganisation)} label={
                  <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <SettingsIcon />
                    <Typography sx={{ fontWeight: 'bold' }}>Settings</Typography>
                  </Stack>
                } />
              </Tabs>
            </Stack>
            <Stack sx={{ pt: 2 }}>
              {tabValue === 0 && <UsersSection teamId={finalId} />}
              {finalOrganisation && tabValue === 1 && <GeneralSection team={finalOrganisation} />}
            </Stack>
          </Stack>
        </Stack>

        <AddUserModal
          open={addMemberOpen}
          teamId={finalId}
          onClose={() => setAddMemberOpen(false)}
        />
      </Stack>
    </MainLayout>
  );
};

export default TeamPage;